import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { ANALYTICS_EVENTS } from '@dunea/shared';
import { $isAuthenticated, $authLoading, $currentUser } from '~/lib/stores/auth';
import { fireSignupConversion } from '~/lib/conversion';
import { capture } from '~/lib/posthog';

/**
 * Post-signup landing. Exists so the ad platforms have a distinct URL to
 * attribute a signup to — auth is a modal, so every other route is shared with
 * returning users and would over-count.
 *
 * Deliberately a waypoint, not a destination: it fires the conversion and sends
 * the user straight on to the builder.
 */
const REDIRECT_MS = 2600;

export default function WelcomePage() {
  const navigate = useNavigate();
  const isAuthenticated = useStore($isAuthenticated);
  const authLoading = useStore($authLoading);
  const user = useStore($currentUser);
  const fired = useRef(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Wait for the session to resolve; the magic-link redirect lands here before
    // the token has been exchanged.
    if (!authLoading) {
      if (!isAuthenticated) {
        // Someone hit /welcome directly without an account — no conversion, no page.
        navigate('/', { replace: true });
      } else {
        if (!fired.current) {
          fired.current = true;
          fireSignupConversion();
          // Guarded by the same ref as the ad conversion so a re-render cannot
          // double-count. This page reported to Meta and Google but to nothing
          // we own, which left the only gap in the activation path unmeasured.
          capture(ANALYTICS_EVENTS.WELCOME_VIEWED);
        }

        timer = setTimeout(() => navigate('/', { replace: true }), REDIRECT_MS);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [authLoading, isAuthenticated, navigate]);

  const firstName = user?.displayName?.split(' ')[0];

  return (
    <div
      className="ui-root"
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 460 }}>
        <div className="inline-flex items-center justify-center" style={{ gap: 8 }}>
          <img src="/dunea-mark.svg" width={26} height={26} alt="" style={{ display: 'block' }} />
          <span className="ui-brand" style={{ fontSize: 21, lineHeight: 1, color: 'var(--color-primary)' }}>
            Dunea
          </span>
        </div>

        <h1 className="ui-serif" style={{ fontSize: 40, margin: '22px 0 10px', lineHeight: 1.08, fontWeight: 400 }}>
          {firstName ? `You're in, ${firstName}.` : "You're in."}
        </h1>

        <p style={{ margin: 0, color: 'var(--color-secondary)', fontSize: 14.5, lineHeight: 1.5 }}>
          Describe the site you want and Dunea builds it — layout, copy, code, and a live URL.
        </p>

        <button
          type="button"
          onClick={() => {
            capture(ANALYTICS_EVENTS.WELCOME_CTA_CLICKED, { action: 'start_building' });
            navigate('/', { replace: true });
          }}
          className="ui-btn-primary"
          style={{ marginTop: 26 }}
        >
          Start building
        </button>

        <p style={{ margin: '14px 0 0', color: 'var(--color-secondary)', fontSize: 12.5, opacity: 0.75 }}>
          Taking you there automatically…
        </p>
      </div>
    </div>
  );
}
